import { useBahasa } from '../../../app/i18n'
import {
  useModelTenant, useModelTersedia, usePilihModel,
} from '../../../domains/tenancy/api'
import { Bendera, Chip, Gagal, Memuat, Panel, Tombol } from '../../../shared/ui'

const PERAN = [
  { role: 'chat', kunci: 'ta.peranChat' },
  { role: 'text', kunci: 'ta.peranText' },
  { role: 'image', kunci: 'ta.peranImage' },
  { role: 'video', kunci: 'ta.peranVideo' },
]

const SEMUA = PERAN.map((p) => p.role)

/**
 * Satu peran: yang dipilih (terurut, bisa digeser) dan yang tersedia.
 *
 * URUTAN ADALAH RANTAI CADANGAN, bukan tampilan (ADR-054). Karena itu tombol geser
 * ada, dan nomornya ditampilkan — orang yang menyusun daftar ini harus tahu bahwa
 * yang di atas dicoba lebih dulu.
 */
function PerPeran({ role, judul, dipilih, tersedia, simpan, t }) {
  const ids = dipilih.map((m) => m.id)
  const sisa = tersedia.filter((m) => !ids.includes(m.id))
  const sedang = simpan.isPending && simpan.variables?.role === role

  const set = (baru) => simpan.mutate({ role, model_ids: baru })
  const geser = (i, arah) => {
    const b = [...ids]
    const j = i + arah
    if (j < 0 || j >= b.length) return
    ;[b[i], b[j]] = [b[j], b[i]]
    set(b)
  }

  return (
    <Panel judul={judul} catatan={t('ta.urutanCadangan')}>
      <div className="p-3.5 flex flex-col gap-3">
        {dipilih.length === 0 ? (
          <p className="m-0 text-[12.5px] text-warn">{t('ta.belumAdaModel')}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {dipilih.map((m, i) => (
              <div key={m.id} className="flex items-center gap-2 bg-surface2 border
                                         border-line rounded-md px-2.5 py-1.5">
                <b className="text-[11px] tabular text-ink3 w-4">{i + 1}</b>
                <span className="text-[12.5px] font-semibold grow truncate">{m.name}</span>
                {m.tier && <Chip nada="idle" titik={false}>{m.tier}</Chip>}
                {/* TARIF BELUM DIKETAHUI punya akibat yang harus terbaca oleh yang
                    memilih: pemakaian model ini TIDAK dihitung pagu belanja. Model video
                    adalah kasus nyatanya — OpenRouter tidak memaparkan harga video di
                    mana pun. Tanpa tanda ini, pagu diam-diam berhenti melindungi. */}
                {m.tarif_diketahui === false && (
                  <Chip nada="warn" titik={false} title={t('umum.tarifTidakDiketahuiKet')}>
                    {t('umum.tarifTidakDiketahui')}
                  </Chip>
                )}
                <Tombol kecil jenis="sekunder" disabled={sedang || i === 0}
                        onClick={() => geser(i, -1)} aria-label={t('ta.naik')}>↑</Tombol>
                <Tombol kecil jenis="sekunder" disabled={sedang || i === dipilih.length - 1}
                        onClick={() => geser(i, 1)} aria-label={t('ta.turun')}>↓</Tombol>
                <Tombol kecil jenis="sekunder" disabled={sedang}
                        onClick={() => set(ids.filter((x) => x !== m.id))}>
                  {t('ta.buang')}
                </Tombol>
              </div>
            ))}
          </div>
        )}

        {sisa.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.07em] text-ink3">
              {t('ta.tambahkan')}
            </span>
            {sisa.map((m) => (
              <Tombol key={m.id} kecil jenis="sekunder" disabled={sedang}
                      onClick={() => set([...ids, m.id])}>
                + {m.name}
              </Tombol>
            ))}
          </div>
        )}
      </div>
    </Panel>
  )
}

/**
 * MODEL — dipakai sebagai TAB di dua tempat, dengan peran yang berbeda.
 *
 * Desain (`arsip/docs-v2/desain/tenant-admin.html`) memecahnya menurut TEMPAT PAKAINYA, bukan
 * menumpuk empat peran di satu layar: Chatbot → Model memuat "Model untuk balasan chat",
 * Konten → Model memuat "Model untuk konten" (teks, gambar, video). Alasannya ada di
 * desain itu sendiri: model gambar paling terasa di BIAYA, model caption paling terasa di
 * MUTU. Menaruh keduanya di satu daftar membuat orang menimbang dua hal yang tidak
 * sebanding dalam satu tarikan.
 *
 * TIDAK ADA <KepalaHalaman> di sini. Komponen ini selalu dirender di dalam tab, dan judul
 * halaman di bawah bilah tab membuat orang mengira ia berpindah layar. Aturannya satu
 * arah: yang punya rute yang memasang judul, yang jadi tab tidak pernah.
 */
export default function Model({ peran = SEMUA, catatan = null }) {
  const { t } = useBahasa()
  const dipilih = useModelTenant()
  const tersedia = useModelTersedia()
  const simpan = usePilihModel()

  if (dipilih.isLoading || tersedia.isLoading) return <Memuat tinggi={6} />
  if (dipilih.isError) return <Gagal galat={dipilih.error} ulangi={dipilih.refetch} />
  if (tersedia.isError) return <Gagal galat={tersedia.error} ulangi={tersedia.refetch} />

  return (
    <div className="flex flex-col gap-3.5">
      {simpan.isError && (
        <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>
      )}

      {PERAN.filter((p) => peran.includes(p.role)).map(({ role, kunci }) => (
        <PerPeran
          key={role}
          role={role}
          judul={t(kunci)}
          dipilih={(dipilih.data ?? []).filter((m) => m.role === role)}
          tersedia={(tersedia.data ?? []).filter((m) => m.role === role)}
          simpan={simpan}
          t={t}
        />
      ))}

      {catatan}
    </div>
  )
}
